import React, { useState, type KeyboardEvent, type ChangeEvent } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import { ADMIN_ORDER_SEARCH_CRITERIA } from '@lds-efsp/shared-contracts';
import type { IconsType } from '../../../types/admin';

interface SearchResult {
  field: string;
  value: string;
}

interface DashboardSearchBarProps {
  brand: BrandConfig;
  Icons: IconsType;
  onSearch?: (result: SearchResult) => void;
}

const DashboardSearchBar: React.FC<DashboardSearchBarProps> = ({ brand, Icons, onSearch }) => {
  // The dropdown offers exactly the shared admin-order-search contract's
  // operator-searchable criteria, derived from the registry — never a copied
  // literal — so a criterion added or removed there moves this dropdown with
  // it. Every option value scopes the search to one searched column.
  const [searchField, setSearchField] = useState<string>(ADMIN_ORDER_SEARCH_CRITERIA[0].id);
  const [searchValue, setSearchValue] = useState('');

  const searchFields = ADMIN_ORDER_SEARCH_CRITERIA.map((entry) => ({
    value: entry.id,
    label: entry.label,
  }));

  const handleSearch = (): void => {
    if (onSearch && searchValue.trim()) {
      onSearch({ field: searchField, value: searchValue.trim() });
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const selectClass = `
    appearance-none px-3 py-2 pr-8 text-sm rounded-l-lg border border-r-0
    focus:outline-hidden focus:ring-2 focus:ring-offset-0
  `;

  return (
    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
      <label htmlFor="dashboard-search-field" className="text-sm" style={{ color: brand.colors.textLight }}>
        Search:
      </label>
      <div className="flex">
        <div className="relative">
          <select
            id="dashboard-search-field"
            value={searchField}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => setSearchField(e.target.value)}
            className={selectClass}
            style={{
              borderColor: brand.colors.border,
              color: brand.colors.text,
              minHeight: '44px'
            }}
            data-testid="search-field-selector"
          >
            {searchFields.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <Icons.ChevronDown
            className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
            style={{ color: brand.colors.textLight }}
          />
        </div>
        <label htmlFor="dashboard-search-value" className="sr-only">Search value</label>
        <input
          id="dashboard-search-value"
          type="text"
          value={searchValue}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter search value..."
          className="px-4 py-2 border text-sm w-full sm:w-48"
          style={{
            borderColor: brand.colors.border,
            minHeight: '44px'
          }}
          data-testid="search-value-input"
        />
        <button
          onClick={handleSearch}
          aria-label="Search"
          className="px-4 py-2 rounded-r-lg font-medium text-sm"
          style={{
            backgroundColor: brand.colors.primary,
            color: brand.colors.onPrimary,
            minHeight: '44px'
          }}
          data-testid="search-button"
        >
          <Icons.Search className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default DashboardSearchBar;
